import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Check, Crown, Headset, Lock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Common";
import { AllowanceCard } from "@/components/recruiter/Allowance";
import { Booker } from "@/components/recruiter/Booker";
import { PrepForm } from "@/components/recruiter/Prep";
import { Appointments } from "@/components/recruiter/Appointments";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { CALENDLY, TIER_BENEFITS, upgradeInterest, useRecruiter } from "@/lib/recruiter";
import { cn } from "@/lib/utils";

const Benefits = ({ tier }) => (
  <div className="grid gap-3 md:grid-cols-3" data-testid="recruiter-tiers">
    {["free", "pro", "elite"].map((t) => (
      <div key={t} className={cn("rounded-2xl border p-4", t === tier ? "border-[#8224e3] bg-[#8224e3]/10" : "border-[#27272A] bg-[#121214]", t === "elite" && t !== tier && "border-[#D4AF37]/40")} data-testid={`recruiter-tier-${t}`}>
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-300">{t === "elite" && <Crown className="h-3.5 w-3.5 text-[#F3C649]" />}{t}{t === tier && <span className="ml-auto rounded-full bg-[#8224e3] px-2 py-0.5 text-[9px] text-white">Your plan</span>}</p>
        <ul className="mt-3 space-y-1.5">{TIER_BENEFITS[t].map((b) => <li key={b} className="flex gap-2 text-xs text-zinc-300"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#c79bff]" />{b}</li>)}</ul>
      </div>
    ))}
  </div>
);

const Resources = ({ items }) => !items?.length ? null : (
  <div className="rounded-2xl border border-[#27272A] bg-[#121214] p-5" data-testid="recruiter-resources">
    <p className="mb-3 flex items-center gap-2 font-display text-lg text-zinc-100"><BookOpen className="h-4 w-4 text-[#c79bff]" />Guides, webinars & Q&A</p>
    <div className="space-y-2">{items.map((r, i) => (
      <a key={r.id || i} href={r.url} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 rounded-lg border border-[#1f1f23] px-3 py-2 hover:border-[#8224e3]/50" data-testid={`recruiter-resource-${i}`}>
        <span className="min-w-0"><span className="block truncate text-sm text-zinc-100">{r.title}</span><span className="block text-[11px] text-zinc-500">{[r.kind, r.starts_at && new Date(r.starts_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })].filter(Boolean).join(" · ")}</span></span>
        {r.locked && <Lock className="h-3.5 w-3.5 text-zinc-500" />}
      </a>
    ))}</div>
  </div>
);

const FreeUpsell = () => {
  const nav = useNavigate();
  const [sent, setSent] = useState(false);
  const ask = async () => { try { await upgradeInterest("recruiter_support"); } catch (e) { if (e.response?.status !== 404) return toast.error(errMsg(e)); } setSent(true); toast.success("Thanks! We'll email you how recruiter support works."); };
  return (
    <div className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#33290D]/50 to-[#121214] p-5" data-testid="recruiter-upsell">
      <p className="flex items-center gap-2 font-display text-xl text-zinc-50"><Lock className="h-4 w-4 text-[#F3C649]" />1:1 recruiter sessions are part of Pro and Elite</p>
      <p className="mt-1 text-sm text-zinc-400">Meet with a real recruiter and get a human perspective on your job search. Elite includes a 30-minute session every month at no extra cost.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={() => nav("/upgrade?tier=elite")} className="bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="recruiter-upgrade"><Crown className="h-4 w-4" />See Elite</Button>
        <Button variant="outline" onClick={ask} disabled={sent} className="border-[#8224e3]/50 text-[#d7b6ff]" data-testid="recruiter-request-info">{sent ? "Request sent" : "Email me the details"}</Button>
      </div>
    </div>
  );
};

export default function Recruiter() {
  const { tier } = useAuth();
  const [me, reload] = useRecruiter(tier);
  if (!me) return <Spinner className="h-6 w-6 text-[#8224e3]" />;
  const a = me.allowance || {};
  const paid = tier !== "free" && a.included > 0;
  const canBook = paid && !me.error && (me.pending || a.remaining > 0);
  return (
    <div className="space-y-8" data-testid="recruiter-page">
      {me.error && <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="recruiter-load-error">We couldn't load your recruiter allowance right now. Please refresh in a moment before booking.</p>}
      <div className="relative overflow-hidden rounded-3xl border border-[#8224e3]/40 bg-gradient-to-br from-[#1a0b2e] via-[#0f0b16] to-[#09090B] p-6 sm:p-8">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#8224e3]/30 blur-3xl" />
        <span className="relative font-mono text-[10px] uppercase tracking-[0.25em] text-[#d7b6ff]">Human recruiter support</span>
        <h1 className="relative mt-2 font-display text-4xl text-zinc-50 sm:text-5xl">Speak to a recruiter live.</h1>
        <p className="relative mt-3 max-w-2xl text-sm text-zinc-300 sm:text-base">AI-powered career support. Real recruiting professionals behind it. Get personalized guidance from people who understand how companies hire.</p>
        <div className="relative mt-4 flex flex-wrap gap-3 text-xs text-zinc-400"><span className="flex items-center gap-1.5"><Headset className="h-3.5 w-3.5 text-[#c79bff]" />Real recruiters, not bots</span><span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-[#c79bff]" />Included in your plan, no extra charge</span></div>
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0 space-y-6">
          {canBook ? <Booker url={me.booking_url || CALENDLY[tier]} title={`Book your ${a.duration_min}-minute recruiter session`} onBooked={reload} />
            : paid ? <div className="rounded-2xl border border-[#27272A] bg-[#121214] p-5 text-sm text-zinc-300" data-testid="recruiter-used-up">You've used your session{a.period === "month" ? " this month" : " this quarter"}. Your next one opens {a.resets_at ? new Date(a.resets_at).toLocaleDateString([], { dateStyle: "medium" }) : "next period"}.{tier === "pro" && " Elite includes a 30-minute session every month."}</div>
            : <><FreeUpsell /><Booker url={me.booking_url || CALENDLY.free} title="Free recruiter intro call" onBooked={reload} /></>}
          <Appointments me={me} />
        </div>
        <div className="space-y-6">
          <AllowanceCard me={me} />
          {paid && <PrepForm me={me} onSaved={reload} />}
          <Resources items={me.resources} />
        </div>
      </div>
      <Benefits tier={tier} />
    </div>
  );
}
